import { useClientPushNotifications } from "@/hooks/useClientPushNotifications";
import { isAndroidPlatform, isIOS, isStandaloneDisplayMode } from "@/lib/platform";
import { clientAuthService, type ClientUser } from "@/services/clientAuth.service/clientAuth.api";
import { Bell, BellOff, Loader2, Share, Smartphone } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Props {
  user?: ClientUser | null;
}

function shouldShowModal(user: ClientUser | null | undefined): boolean {
  if (isStandaloneDisplayMode()) return false;
  if (!isIOS() && !isAndroidPlatform()) return false;
  if (user?.preferences?.pwaInstallPromptDismissed) return false;
  return true;
}

export function AddToHomeScreenModal({ user: userProp }: Props) {
  const user = userProp ?? clientAuthService.getCurrentUser();
  const [open, setOpen] = useState(() => shouldShowModal(user));
  const [dismissForever, setDismissForever] = useState(false);
  const [closing, setClosing] = useState(false);

  const { state: pushState, loading: pushLoading, subscribe } = useClientPushNotifications();

  const handleClose = async () => {
    if (dismissForever) {
      setClosing(true);
      await clientAuthService.updatePreferences({ pwaInstallPromptDismissed: true });
      setClosing(false);
    }
    setOpen(false);
  };

  const ios = isIOS();
  const android = isAndroidPlatform();

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) void handleClose();
      }}
    >
      <DialogContent className="max-w-sm sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 shrink-0 text-primary" />
            Instale o BaitaTicket no seu celular
          </DialogTitle>
          <DialogDescription>
            Com o app instalado na tela inicial você acessa seus tickets com um toque — e,
            ativando as notificações, recebe um alerta imediato toda vez que alguém responder
            ao seu chamado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Platform-specific installation instructions */}
          {ios && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2 text-sm">
              <p className="font-medium flex items-center gap-1.5">
                <Share className="h-4 w-4 shrink-0 text-muted-foreground" />
                Como instalar no iPhone / iPad (Safari)
              </p>
              <ol className="space-y-1 list-decimal list-inside text-muted-foreground leading-snug">
                <li>
                  Toque no ícone de compartilhar{" "}
                  <span className="font-medium text-foreground">(quadrado com seta para cima)</span>{" "}
                  na barra inferior do Safari.
                </li>
                <li>
                  Role a lista e toque em{" "}
                  <span className="font-medium text-foreground">"Adicionar à Tela de Início"</span>.
                </li>
                <li>
                  Toque em <span className="font-medium text-foreground">"Adicionar"</span> no canto
                  superior direito.
                </li>
              </ol>
            </div>
          )}

          {android && !ios && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2 text-sm">
              <p className="font-medium flex items-center gap-1.5">
                <Smartphone className="h-4 w-4 shrink-0 text-muted-foreground" />
                Como instalar no Android (Chrome)
              </p>
              <ol className="space-y-1 list-decimal list-inside text-muted-foreground leading-snug">
                <li>
                  Toque no menu{" "}
                  <span className="font-medium text-foreground">&#8942;</span>{" "}
                  no canto superior direito do Chrome.
                </li>
                <li>
                  Toque em{" "}
                  <span className="font-medium text-foreground">
                    "Adicionar à tela inicial"
                  </span>{" "}
                  ou{" "}
                  <span className="font-medium text-foreground">"Instalar app"</span>.
                </li>
                <li>
                  Confirme tocando em{" "}
                  <span className="font-medium text-foreground">"Adicionar"</span> ou{" "}
                  <span className="font-medium text-foreground">"Instalar"</span>.
                </li>
              </ol>
            </div>
          )}

          {/* Push notification button */}
          <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-1.5">
            <p className="text-sm font-medium">Notificações de tickets</p>
            <p className="text-xs text-muted-foreground">
              Receba um aviso na hora quando seu ticket for respondido — mesmo com o navegador
              fechado.
            </p>
            {pushState === "subscribed" ? (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium pt-1">
                <Bell className="h-4 w-4 shrink-0" />
                Notificações ativadas
              </div>
            ) : pushState === "denied" ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground pt-1">
                <BellOff className="h-4 w-4 shrink-0" />
                Permissão negada. Habilite nas configurações do navegador.
              </div>
            ) : pushState === "unsupported" ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground pt-1">
                <BellOff className="h-4 w-4 shrink-0" />
                Seu navegador não suporta notificações push.
              </div>
            ) : (
              <Button
                type="button"
                size="sm"
                className="mt-1 gap-2"
                disabled={pushLoading}
                onClick={() => void subscribe()}
              >
                {pushLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Bell className="h-3.5 w-3.5" />
                )}
                Ativar notificações
              </Button>
            )}
          </div>

          {/* "Don't show again" checkbox */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              id="pwa-dismiss-forever"
              checked={dismissForever}
              onCheckedChange={(checked) => setDismissForever(checked === true)}
            />
            <span className="text-sm text-muted-foreground">Não mostrar isso novamente</span>
          </label>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={closing}
            onClick={() => void handleClose()}
          >
            {closing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Agora não"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
